/**
 * Resolves the client renderer for a running instance and hands it the shared `ModuleRendererProps` contract
 * (docs/ARCHITECTURE.md §12). If `@momentum/modules/client` has no entry for `instance.renderer`, shows a
 * fallback panel instead of crashing so the shell keeps working while modules are still being built.
 */
import { useCallback } from 'react';
import { clientRenderers } from '@momentum/modules/client';
import type { ActionOutcome, InstanceView, SelfInfo, ServerEventMessage, SessionPublic } from '@momentum/shared';
import type { SessionClient } from './client.ts';
import { strings } from '../shell/strings.ts';

export interface InstanceHostProps {
  instance: InstanceView;
  session: SessionPublic;
  you: SelfInfo | null;
  client: SessionClient;
  serverTimeOffset: number;
  reducedMotion: boolean;
  /**
   * Presenter mode only (T7): when set, every action this instance sends is dispatched `asParticipantId`,
   * so the presenter's own connection operates that simulated participant's controls instead of its own
   * identity. Omitted (or `undefined`) for every ordinary session, which is a no-op for `client.send`.
   */
  asParticipantId?: string;
}

export function InstanceHost({ instance, session, you, client, serverTimeOffset, reducedMotion, asParticipantId }: InstanceHostProps) {
  const instanceId = instance.instanceId;

  const send = useCallback(
    (name: string, payload: unknown): Promise<ActionOutcome> =>
      client.send(name, payload, { instanceId }, asParticipantId ? { asParticipantId } : undefined),
    [client, instanceId, asParticipantId],
  );

  const onEvent = useCallback(
    (listener: (event: ServerEventMessage) => void) =>
      client.onEvent((event) => {
        if (event.instanceId === instanceId) listener(event);
      }),
    [client, instanceId],
  );

  const renderer = clientRenderers[instance.renderer];
  // `you` is always the AUTHENTICATED connection's own identity — for a presenter that's role 'presenter',
  // never the simulated participant it may be viewing as (docs/ARCHITECTURE.md §4, §10). So when the
  // presenter is deliberately acting `asParticipantId`, the ordinary `you.role !== 'player'` gate would wrongly
  // disable every control; `instance.isParticipant` (server-computed from the *viewed* perspective) is the
  // right guard in that case instead.
  const readOnly = !instance.isParticipant || (asParticipantId === undefined && (you === null || you.role !== 'player'));

  if (!renderer) {
    return (
      <div className="card stack-sm">
        <p>{strings.play.noRenderer(instance.renderer)}</p>
        <pre style={{ overflow: 'auto', maxHeight: 320 }}>{JSON.stringify(instance.view, null, 2)}</pre>
      </div>
    );
  }

  const Component = renderer.Component;
  return (
    <Component
      instance={instance}
      session={session}
      you={you}
      send={send}
      onEvent={onEvent}
      serverTimeOffset={serverTimeOffset}
      reducedMotion={reducedMotion}
      readOnly={readOnly}
    />
  );
}
